import { createSelector } from "@reduxjs/toolkit";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  exportDesignerNodeCreate,
  getDesignerNodeDataByIdSelector,
  nodeDesignsStateSelector,
  type GetPortableDesignerNodeInContext,
} from "@skilitics/nautilus-core-designer-data";

import { useContentHostId } from "../backend/index.ts";
import { getManifestSelector } from "../state/contextualSelectors/appConfigSelectors.ts";
import { getNodeTitleByIdSelector } from "../state/contextualSelectors/nodeTitleSelectors.ts";
import { useNautilusSelector, type NautilusDesignerAppState } from "../state/shared.ts";
import type { DesignerNode } from "../types/index.ts";

const originSelector = (_: NautilusDesignerAppState, param: { origin: string }) => param.origin;

const getCurrentNodeVersionSelector = createSelector(
  [getManifestSelector],
  (getManifest) =>
    ({ typeId }: Pick<DesignerNode, "typeId">) =>
      getManifest(typeId).version,
);

const exportDesignerNodeSelector = createSelector(
  [nodeDesignsStateSelector, getCurrentNodeVersionSelector, originSelector, getNodeTitleByIdSelector],
  (state, getCurrentNodeVersion, origin, getNodeTitleById) =>
    exportDesignerNodeCreate({ state, getCurrentNodeVersion, origin, getNodeTitleById }),
);

export const useExportNodeDesign = () => {
  const hostId = useContentHostId();
  const exportDesignerNode = useNautilusSelector((state) =>
    exportDesignerNodeSelector(state, { origin: window.location.origin }),
  );
  const getByIdMultilingual = useSelector(getDesignerNodeDataByIdSelector);

  return useMemo(
    () => (nodeId: number, context: Omit<Parameters<GetPortableDesignerNodeInContext>[1], "hostId">) =>
      exportDesignerNode(getByIdMultilingual(nodeId), { ...context, hostId }),
    [hostId, exportDesignerNode, getByIdMultilingual],
  );
};
